import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Visibility,
  Receipt,
  Cancel,
  Business,
  ErrorOutline,
  AccountBalance,
  Search,
} from '@mui/icons-material';
import { useFailedTransactions } from '../../hooks/useSuperAdmin';
import { colors } from '../../theme/theme';
import { TextField, InputAdornment } from '@mui/material';

export const TransactionsFailedPage = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { transactions, isLoading, error } = useFailedTransactions(page + 1);

  const filteredTransactions = transactions?.data?.filter((transaction: any) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      transaction.transaction_id?.toString().toLowerCase().includes(search) ||
      transaction.reference?.toLowerCase().includes(search) ||
      transaction.id?.toString().toLowerCase().includes(search) ||
      transaction.transaction_code?.toLowerCase().includes(search) ||
      transaction.user?.email?.toLowerCase().includes(search) ||
      transaction.user?.first_name?.toLowerCase().includes(search) ||
      transaction.user?.last_name?.toLowerCase().includes(search) ||
      transaction.county?.name?.toLowerCase().includes(search) ||
      transaction.customer?.name?.toLowerCase().includes(search) ||
      transaction.customer_name?.toLowerCase().includes(search) ||
      transaction.amount?.toString().includes(search)
    );
  }) || [];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleViewDetails = () => {
    setDetailsOpen(true);
    handleMenuClose();
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTransaction(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'transaction_id',
      headerName: 'Transaction ID',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt sx={{ fontSize: 16, color: colors.error.main }} />
          <Box sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {params.value || params.row.reference || params.row.id}
          </Box>
        </Box>
      ),
    },
    {
      field: 'county',
      headerName: 'County',
      width: 140,
      valueGetter: (value, row) => row.county?.name || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 14, color: colors.grey[500] }} />
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => row.customer?.name || row.customer_name || 'N/A',
      renderCell: (params) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>{params.value}</Box>
          <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
            {params.row.customer?.phone || params.row.customer_phone || ''}
          </Box>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ fontWeight: 700, color: colors.error.main }}>
          KES {params.value?.toLocaleString() || 0}
        </Box>
      ),
    },
    {
      field: 'payment_method',
      headerName: 'Payment',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ textTransform: 'uppercase' }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 160,
      valueGetter: (value) => (value ? new Date(value).toLocaleString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <Visibility fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const totalRevenue = transactions?.data?.reduce((sum: number, t: any) => sum + (Number(t.amount) || 0), 0) || 0;

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Failed Transactions"
          subtitle="View all failed transactions across the platform"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Transactions', path: '/superadmin/transactions' },
            { label: 'Failed' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load failed transactions. Please try again.
          </Alert>
        )}

        {/* Search Bar */}
        <Box mb={3}>
          <TextField
            fullWidth
            placeholder="Search by transaction code, user, county, or amount..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: colors.grey[400] }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '&:hover fieldset': {
                  borderColor: colors.primary.main,
                },
              },
            }}
          />
        </Box>

        {/* Summary Cards */}
        {!isLoading && transactions && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.error.main} 0%, ${colors.error.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.error.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.error.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Failed</Box>
                    <Cancel sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {transactions.meta?.total?.toLocaleString() || transactions.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Failed payments</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.warning.main} 0%, ${colors.warning.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.warning.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.warning.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Attempted Amount</Box>
                    <ErrorOutline sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    KES {totalRevenue.toLocaleString()}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>From failed transactions</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.info.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.info.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Average Amount</Box>
                    <AccountBalance sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    KES {transactions.data?.length > 0 ? Math.round(totalRevenue / transactions.data.length).toLocaleString() : 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Per transaction</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.secondary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.secondary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Counties</Box>
                    <Business sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {new Set(transactions.data?.map((t: any) => t.county_id).filter(Boolean)).size || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>With failed payments</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={filteredTransactions}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={transactions?.meta?.total || transactions?.data?.length || 0}
                  paginationMode={transactions?.meta?.total ? 'server' : 'client'}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.error.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
        </Menu>

        {/* Transaction Details Dialog */}
        <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
          <DialogTitle sx={{ borderBottom: `1px solid ${colors.grey[200]}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt sx={{ color: colors.error.main }} />
              <Typography variant="h6">Transaction Details</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedTransaction && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Transaction ID
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                    {selectedTransaction.transaction_id || selectedTransaction.reference || selectedTransaction.id}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={<Cancel sx={{ fontSize: 14 }} />}
                      label="Failed"
                      color="error"
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    County
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedTransaction.county?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Amount
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 700, color: colors.error.main }}>
                    KES {Number(selectedTransaction.amount)?.toLocaleString() || 0}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Customer Name
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedTransaction.customer?.name || selectedTransaction.customer_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Customer Phone
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedTransaction.customer?.phone || selectedTransaction.customer_phone || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Payment Method
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedTransaction.payment_method || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
                {selectedTransaction.mpesa_receipt_number && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      M-Pesa Receipt
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                      {selectedTransaction.mpesa_receipt_number}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: `1px solid ${colors.grey[200]}`, px: 3, py: 2 }}>
            <Button onClick={handleCloseDetails} variant="outlined" color="error">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
